// Reading and Writing domain guide. Each section explains how to recognize a
// question type and how to work through it, with worked mini-examples where
// useful. Paragraphs are plain strings, callout text objects
// { text, label, kind }, or structured blocks { type: 'list', ... } — list
// blocks render as a bulleted list instead of one run-on sentence. kind is
// 'example', 'tip', or 'mistake' and controls the callout badge color in
// StudyGuide.css.

export const documentTitle = 'Reading and Writing';
export const documentSlug = 'reading-and-writing';

export const parts = [
  {
    id: 'part-rw-approach',
    title: 'How to Read a Reading and Writing Passage',
    sections: [
      {
        id: 'rw-how-it-works',
        heading: 'How Each Question Works',
        paragraphs: [
          'The digital SAT does not use long passages with multiple questions attached, the way the old paper SAT did. Instead, each Reading and Writing question comes with its own short, self-contained passage — usually a single paragraph of 25 to 150 words — followed by exactly one question about it. The only exception is the small number of cross-text connection questions, which pair two short passages for a single question. Because every passage is self-contained, everything you need to answer the question is right there in the text — you never need outside knowledge of the topic.',
          'The section has two modules of 27 questions each (54 total), with about 32 minutes per module — roughly 71 seconds per question. Questions from all four domains (Craft and Structure, Information and Ideas, Standard English Conventions, Expression of Ideas) are mixed together in no particular order within a module, so learn to recognize which type of question you\'re looking at from the prompt wording alone, since each type calls for a different reading approach.',
        ],
      },
      {
        id: 'rw-general-strategy',
        heading: 'General Reading Strategy',
        paragraphs: [
          {
            kind: 'tip',
            label: 'Strategy Tip',
            text: "Read the entire short passage carefully before looking at the answer choices — don't skim. Because the passages are short, a careful first read costs you very little time, and it prevents you from missing the single word that flips the meaning of the whole passage, like \"not,\" \"except,\" \"however,\" or \"despite.\"",
          },
          {
            kind: 'tip',
            label: 'Strategy Tip',
            text: "For most question types, form your own answer in your head before you read the choices. This keeps a well-written but wrong answer choice from talking you into picking it — if your own answer closely matches one of the four choices, you can be confident in it.",
          },
          {
            kind: 'tip',
            label: 'Strategy Tip',
            text: 'Use process of elimination confidently, and look for a specific, articulable reason to cross out each wrong choice — "too broad," "not supported by this passage," "wrong verb tense," "answers a different question than the one asked." Vague gut feelings are less reliable than a concrete reason.',
          },
          {
            kind: 'mistake',
            label: 'Common Mistake',
            text: "Picking an answer because it's true or reasonable in the real world, rather than because the given passage actually supports it. SAT reading answers must be justified by the specific text in front of you, not by outside knowledge or what generally seems plausible.",
          },
        ],
      },
    ],
  },
  {
    id: 'part-craft-structure',
    title: 'Craft and Structure',
    sections: [
      {
        id: 'rw-words-in-context',
        heading: 'Words in Context',
        paragraphs: [
          'Questions in this domain ask you to determine the meaning of a word or phrase as it\'s used in a specific passage, or to choose the word or phrase that best completes the writer\'s intended meaning in a blank. Context — not your general vocabulary knowledge — determines the correct answer. The most reliable method: cover up the blank or the word in question, read the sentence, and predict your own word based on the surrounding logic — then find the answer choice closest to your prediction.',
          {
            kind: 'tip',
            label: 'Strategy Tip',
            text: 'Watch for signal words. A word like "however," "but," "although," or "despite" tells you the blank needs to contrast with a nearby idea. A word like "and," "also," or "similarly" tells you the blank should continue or agree with a nearby idea. These signal words are often the fastest way to narrow down the right tone and meaning for the blank.',
          },
          {
            kind: 'example',
            label: 'Worked Example',
            type: 'steps',
            intro: 'The critics found the sequel curiously ______ ; despite sharing the same director and cast as the beloved original, it failed to capture any of that film\'s energy. (A) exhilarating (B) lackluster (C) innovative (D) authentic',
            steps: [
              'The word "despite" signals contrast with "the beloved original."',
              'The phrase "failed to capture any of that film\'s energy" confirms the blank should mean dull or lacking energy.',
              'That matches (B) lackluster.',
            ],
          },
        ],
      },
      {
        id: 'rw-text-structure-purpose',
        heading: 'Text Structure and Purpose',
        paragraphs: [
          'These questions ask how a passage (or a paragraph within it) is organized, or why the author included a particular detail, example, or sentence. Common structures include:',
          {
            type: 'list',
            items: ['Problem and solution', 'Chronological narrative', 'Comparison and contrast', 'Claim followed by a counterclaim'],
          },
          'For a purpose question, ask yourself what job the specific detail is doing for the passage as a whole. Common purposes include:',
          {
            type: 'list',
            items: [
              'Providing evidence for a claim',
              'Illustrating a contrast',
              'Qualifying an earlier statement',
              'Introducing a complication',
            ],
          },
          {
            kind: 'tip',
            label: 'Strategy Tip',
            text: 'For a "why did the author include this" question, describe the detail\'s function in your own words before checking the choices — for example, "it\'s an example that supports the claim in the previous sentence" — then match that function to a choice, rather than matching just the topic.',
          },
        ],
      },
      {
        id: 'rw-cross-text',
        heading: 'Cross-Text Connections',
        paragraphs: [
          'A small number of questions present two short, related passages and ask how the two authors would likely respond to each other, or how their perspectives compare. Read for each author\'s main claim or stance first — try to summarize each one in a short phrase — then look for where the two agree, disagree, or address different aspects of the same topic.',
          {
            kind: 'tip',
            label: 'Strategy Tip',
            text: 'Before reading the answer choices, decide in one sentence how Author 2 would most likely react to Author 1\'s claim (agree, disagree, or qualify it). Then find the choice that matches — this keeps you from being distracted by a choice that\'s true of Author 1 alone, or Author 2 alone, but doesn\'t describe the relationship between them.',
          },
        ],
      },
    ],
  },
  {
    id: 'part-information-ideas',
    title: 'Information and Ideas',
    sections: [
      {
        id: 'rw-central-ideas',
        heading: 'Central Ideas and Details',
        paragraphs: [
          'These questions test whether you can identify a passage\'s main point, as distinct from its supporting details, or summarize what a passage is primarily about. Watch for answer choices that restate a true detail from the passage but are too narrow to be the actual central idea, or that broaden the passage\'s point into a bigger claim than the text actually supports.',
          {
            kind: 'mistake',
            label: 'Common Mistake',
            text: 'Choosing a detail from the passage rather than its main point. A detail can be completely accurate and still be the wrong answer to a "central idea" question if it\'s only one piece of evidence rather than the passage\'s overall point.',
          },
        ],
      },
      {
        id: 'rw-evidence-textual',
        heading: 'Command of Evidence (Textual)',
        paragraphs: [
          'These questions give you a claim or hypothesis and ask you to identify which quotation from the passage best supports it. Read the claim carefully first, then look for the specific sentence that most directly and specifically backs it up. Wrong answers are often true statements from the passage that support a different point, or that simply restate the claim itself rather than providing evidence for it.',
        ],
      },
      {
        id: 'rw-evidence-quantitative',
        heading: 'Command of Evidence (Quantitative)',
        paragraphs: [
          'These questions work the same way as textual evidence questions, but point you to a table or graph instead of a sentence. Read the claim first, then look for the specific data point — not just a general trend — that matches it. Pay close attention to axis labels and units, since a common wrong-answer trap uses a real number from the chart that answers a slightly different question than the one asked.',
          {
            kind: 'example',
            label: 'Worked Example',
            type: 'steps',
            intro: 'A table shows a school\'s enrollment for four years: 200, 240, 280, 410. A claim states that "enrollment more than doubled between Year 1 and Year 4."',
            steps: [
              'Check the actual values, not just the direction: 410 / 200 = 2.05.',
              'It did more than double, so the claim is supported.',
              'The correct evidence choice would be the specific Year 1 and Year 4 values — not just "enrollment increased every year," which is a real but less precise fact from the same table.',
            ],
          },
        ],
      },
      {
        id: 'rw-inferences',
        heading: 'Inferences',
        paragraphs: [
          'Inference questions ask what a passage logically implies without stating it directly. The correct answer must be strongly and necessarily supported by the text — something that must be true given what\'s stated, not just something that could plausibly be true. Resist answer choices that require outside knowledge, or that go a step further than the passage actually justifies.',
          {
            kind: 'mistake',
            label: 'Common Mistake',
            text: 'Picking an inference that\'s a reasonable guess but not a necessary conclusion from the passage. If the passage doesn\'t force that conclusion — if you can imagine the passage being true while the answer choice is false — it\'s too big a leap.',
          },
        ],
      },
    ],
  },
  {
    id: 'part-standard-english',
    title: 'Standard English Conventions',
    sections: [
      {
        id: 'rw-sentence-structure',
        heading: 'Boundaries',
        paragraphs: [
          'These questions test whether you can correctly join, separate, or punctuate clauses. An independent clause has both a subject and a verb and can stand alone as a sentence; a dependent clause cannot, often because it starts with a subordinating word like "although," "because," or "which." Two independent clauses can be joined legally in four ways:',
          {
            type: 'list',
            items: [
              'A period',
              'A semicolon',
              'A comma plus a coordinating conjunction (for, and, nor, but, or, yet, so — remember "FANBOYS")',
              'A colon, when the second clause explains the first',
            ],
          },
          'Joining them with just a comma (a "comma splice") or with no punctuation at all (a "run-on" or "fused sentence") is always wrong.',
          {
            kind: 'example',
            label: 'Worked Example',
            type: 'steps',
            intro: 'Is this correctly punctuated? "The bridge closed for repairs, drivers had to find alternate routes."',
            steps: [
              'No — this is a comma splice, because both halves are independent clauses joined only by a comma.',
              'It can be fixed with a semicolon ("...repairs; drivers..."), a period, or by adding "so" after the comma.',
            ],
          },
          {
            kind: 'tip',
            label: 'Strategy Tip',
            text: 'To spot a fragment, check whether the group of words has both a subject and a main verb and can stand completely on its own. A group of words starting with a subordinating conjunction ("Although the bridge closed...") or a relative pronoun ("...which closed for repairs") is a dependent clause and needs an independent clause attached to it to be a complete sentence.',
          },
        ],
      },
      {
        id: 'rw-agreement',
        heading: 'Form, Structure, and Sense',
        paragraphs: [
          'This covers subject-verb agreement, pronoun-antecedent agreement, verb tense consistency, modifier placement, and parallel structure:',
          {
            type: 'list',
            items: [
              'Subject-verb agreement: find the true subject of the verb — watch for prepositional phrases between the subject and verb ("The results of the study," where "results" is plural and governs the verb, not "study") — and remember that indefinite pronouns like "each," "everyone," and "everything" are singular.',
              'Pronoun-antecedent agreement: a pronoun must match the number of the noun it refers to.',
              'Parallel structure: items in a list or comparison need the same grammatical form — "hiking, swimming, and biking," not "hiking, swimming, and to bike."',
            ],
          },
          {
            kind: 'example',
            label: 'Worked Example',
            type: 'steps',
            intro: 'Spot the error: "Having finished the marathon, a warm bath felt wonderful."',
            steps: [
              'The opening phrase "Having finished the marathon" needs to modify a person, but the subject right after the comma is "a warm bath" — the bath didn\'t finish the marathon.',
              'This dangling modifier is fixed by giving the sentence a human subject: "Having finished the marathon, she found a warm bath wonderful."',
            ],
          },
        ],
      },
    ],
  },
  {
    id: 'part-expression-ideas',
    title: 'Expression of Ideas',
    sections: [
      {
        id: 'rw-rhetorical-synthesis',
        heading: 'Rhetorical Synthesis',
        paragraphs: [
          'These questions give you a set of bullet-point notes on a topic and a specific, stated goal — for example, emphasizing a similarity, introducing an unfamiliar term, or summarizing a finding — then ask you to select the sentence that best accomplishes that goal. The goal is given directly, with no interpretation required, so build a short checklist from it and test each choice only against that checklist. Correct-but-irrelevant information from the notes is the most common wrong-answer trap.',
          {
            kind: 'example',
            label: 'Worked Example',
            type: 'steps',
            intro: 'Notes describe two of a scientist\'s discoveries. Goal: "emphasize a similarity between the two discoveries."',
            steps: [
              'A choice that accurately describes both discoveries but doesn\'t connect them is wrong.',
              'The correct choice must explicitly state what they have in common, since that\'s the specific goal — not just "mention both discoveries."',
            ],
          },
        ],
      },
      {
        id: 'rw-transitions',
        heading: 'Transitions',
        paragraphs: [
          'These questions ask you to choose the transition word or phrase that correctly signals the logical relationship between two ideas. Common categories:',
          {
            type: 'list',
            items: [
              'Addition: furthermore, moreover',
              'Contrast: however, nevertheless, yet',
              'Cause and effect: therefore, consequently, as a result',
              'Example: for instance, specifically',
              'Concession: admittedly, granted',
              'Sequence: subsequently, meanwhile',
            ],
          },
          {
            kind: 'tip',
            label: 'Strategy Tip',
            text: "Determine the logical relationship between the two sentences before looking at the answer choices — decide for yourself whether the second idea contrasts with, follows from, or adds to the first. Then pick the transition that matches that relationship, rather than picking whichever word simply sounds fine when you read it aloud.",
          },
        ],
      },
    ],
  },
];
